import { useMutation } from "@tanstack/react-query";
import { Check, Pin } from "lucide-react";
import type { JSX } from "react";
import { useState } from "react";
import { Link } from "react-router";

import { DraftDiff, DraftDiffDisclosure } from "@/components/ui/draft-diff";
import { type FactProposalState, approveFactProposal, cancelFactProposal } from "@/lib/chat";

// ---------------------------------------------------------------------------
// Text preview / expand toggle
// ---------------------------------------------------------------------------

/** Characters before the "ver más" toggle appears. 300 chars ≈ 3-4 lines of
 * body-2 text in a typical 3xl-max-width card. Attachment-derived proposals
 * can be thousands of characters, so we cap aggressively. */
const PREVIEW_CHAR_LIMIT = 300;

function ProposalText({ text }: { text: string }): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const isLong = text.length > PREVIEW_CHAR_LIMIT;

  if (!isLong) {
    return <p className="text-body-2 text-foreground">{text}</p>;
  }

  const preview = text.slice(0, PREVIEW_CHAR_LIMIT);

  return (
    <div>
      <p className="text-body-2 text-foreground">{expanded ? text : `${preview}…`}</p>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="mt-1 text-label-2 text-brand-extra-2-40 underline-offset-2 hover:underline"
        data-testid="proposal-text-toggle"
      >
        {expanded ? "ver menos" : "ver más"}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card variants
// ---------------------------------------------------------------------------

interface FactProposalCardProps {
  messageId: string;
  proposal: FactProposalState;
  onUpdate: (next: FactProposalState) => void;
  onRefineClick: () => void;
  /** True while a refine request is in flight against this card.
   * Owned by the chat route (`send()` flips it around the `await`),
   * propagated here so the pending card can disable its buttons and
   * surface an inline "refinando…" indicator instead of looking idle
   * during the multi-second LLM round-trip. */
  refining?: boolean;
  lifecycleDisabled?: boolean;
}

export function FactProposalCard({
  messageId,
  proposal,
  onUpdate,
  onRefineClick,
  refining = false,
  lifecycleDisabled = false,
}: FactProposalCardProps): JSX.Element {
  if (proposal.status === "approved") {
    return (
      <div className="mt-3 rounded-md border border-brand-feedback-success-00/30 bg-brand-feedback-success-00/5 px-3 py-2">
        <div className="flex items-start gap-2">
          <Check size={16} className="mt-0.5 text-brand-feedback-success-00" />
          <div className="flex-1">
            <ProposalText text={proposal.text} />
            {proposal.knowledge_item_id && (
              <Link
                to={`/settings/sources?type=chat&highlight=${proposal.knowledge_item_id}`}
                className="mt-1 inline-block text-label-2 text-brand-extra-2-40 underline-offset-2 hover:underline"
              >
                ver en /sources
              </Link>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (proposal.status === "cancelled") {
    return (
      <div className="mt-3 rounded-md border border-border bg-muted/50 px-3 py-2">
        <ProposalText text={proposal.text} />
        <p className="mt-1 text-label-2 uppercase tracking-wider text-muted-foreground">
          cancelado
        </p>
      </div>
    );
  }

  return (
    <PendingCard
      messageId={messageId}
      proposal={proposal}
      onUpdate={onUpdate}
      onRefineClick={onRefineClick}
      refining={refining}
      lifecycleDisabled={lifecycleDisabled}
    />
  );
}

function PendingCard({
  messageId,
  proposal,
  onUpdate,
  onRefineClick,
  refining = false,
  lifecycleDisabled = false,
}: FactProposalCardProps): JSX.Element {
  const approveMutation = useMutation({
    mutationFn: () => approveFactProposal(messageId, proposal.draft_id),
    onSuccess: (data) => {
      onUpdate({
        ...proposal,
        status: "approved",
        decided_at: new Date().toISOString(),
        knowledge_item_id: data.knowledge_item_id,
      });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: () => cancelFactProposal(messageId, proposal.draft_id),
    onSuccess: () => {
      onUpdate({
        ...proposal,
        status: "cancelled",
        decided_at: new Date().toISOString(),
      });
    },
  });

  const disabled =
    approveMutation.isPending || cancelMutation.isPending || refining || lifecycleDisabled;
  const errorMessage = approveMutation.error?.message ?? cancelMutation.error?.message;

  return (
    <div className="mt-3 rounded-md border border-border bg-background px-3 py-2">
      <div className="flex items-start gap-2">
        <Pin size={16} className="mt-0.5 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <p className="mb-1 text-label-2 uppercase tracking-wider text-muted-foreground">
            nuevo dato detectado
          </p>
          <ProposalText text={proposal.text} />
          {proposal.previousText !== undefined && (
            <DraftDiffDisclosure
              summary="Ver cambios"
              className="mt-2"
              summaryClassName="py-2 text-label-2 text-brand-extra-2-40 underline-offset-2 hover:underline"
            >
              <div className="mt-2">
                <DraftDiff
                  before={proposal.previousText}
                  after={proposal.text}
                  beforeLabel="Propuesta anterior"
                  afterLabel="Propuesta refinada"
                  label="Diferencias entre la propuesta anterior y la refinada"
                />
              </div>
            </DraftDiffDisclosure>
          )}
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => approveMutation.mutate()}
              disabled={disabled}
              className="rounded-md bg-brand-extra-2-40 px-3 py-1 text-label-2 font-semibold text-white hover:opacity-90 disabled:opacity-50"
            >
              Aprobar
            </button>
            <button
              type="button"
              onClick={onRefineClick}
              disabled={disabled}
              className="rounded-md border border-border px-3 py-1 text-label-2 text-foreground hover:bg-muted disabled:opacity-50"
            >
              Refinar
            </button>
            <button
              type="button"
              onClick={() => cancelMutation.mutate()}
              disabled={disabled}
              className="rounded-md border border-border px-3 py-1 text-label-2 text-foreground hover:bg-muted disabled:opacity-50"
            >
              Cancelar
            </button>
          </div>
          {refining && (
            <output className="mt-2 block text-label-2 italic text-muted-foreground">
              refinando…
            </output>
          )}
          {lifecycleDisabled && (
            <output className="mt-2 block text-label-2 italic text-muted-foreground">
              sincronizando…
            </output>
          )}
          {errorMessage && (
            <p role="alert" className="mt-2 text-label-2 text-destructive">
              {errorMessage}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
